import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Apple, Play } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import Stats from "../components/homepage/Stats";
import PageHero, { SectionHeader } from "../components/shared/PageHero";
import { CASE_STUDIES_FULL } from "../data/caseStudies";

const CASES = CASE_STUDIES_FULL;

const FILTERS = [
  "All",
  "FinTech",
  "Gaming",
  "Health",
  "EdTech",
  "Food Delivery",
  "Wellness",
];
const SERVICE_FILTERS = ["All", "ASO", "User Acquisition", "Ratings & Reviews", "App Retention", "GEO / AI Citation"];

export default function CaseStudiesPage() {
  const [cat, setCat] = useState("All");
  const [svc, setSvc] = useState("All");
  const filtered = CASES.filter(
    (c) =>
      (cat === "All" || c.category === cat) &&
      (svc === "All" || c.services.includes(svc))
  );

  return (
    <>
      <Helmet>
        <title>
          App Growth Case Studies — Real ASO Results from Lumotis
        </title>
        <meta
          name="description"
          content="Real Lumotis case studies: apps ranked #1 on iOS and Google Play, 4★→4.8★ rating lifts, AI citation wins across ChatGPT and Perplexity. Audited numbers, not vanity metrics."
        />
        <meta
          name="keywords"
          content="ASO case studies, app marketing results, app growth case studies, App Store Optimization success stories, app ranking results"
        />
        <link rel="canonical" href="https://lumotis.com/case-studies" />
      </Helmet>
      <Navbar />
      <main data-testid="case-studies-page">
        <PageHero
          testid="cases-hero"
          eyebrow="Case Studies"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "Case Studies" }]}
          title="Real apps. Real ranks."
          titleAccent="Real revenue lifts."
          subtitle="Audited growth wins from Lumotis clients across FinTech, Gaming, Health, EdTech and Wellness — filterable by category and service."
          primaryCta={{ to: "/contact", label: "Get similar results", testid: "cases-cta-primary" }}
          secondaryCta={{ to: "/aso", label: "See ASO service", testid: "cases-cta-secondary" }}
        />

        {/* Filters */}
        <section className="py-6 bg-white border-y border-zinc-200 sticky top-16 md:top-20 z-30">
          <div className="container grid gap-4 md:grid-cols-2 md:items-center">
            <div className="flex flex-wrap gap-2">
              <span className="text-overline text-zinc-500 mr-1 self-center">
                Category
              </span>
              {FILTERS.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`cases-filter-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                    cat === c
                      ? "bg-charcoal text-white border-charcoal"
                      : "border-zinc-300 text-zinc-700 hover:border-charcoal"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <span className="text-overline text-zinc-500 mr-1 self-center">
                Service
              </span>
              {SERVICE_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setSvc(s)}
                  data-testid={`cases-filter-svc-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                    svc === s
                      ? "bg-electric text-white border-electric"
                      : "border-zinc-300 text-zinc-700 hover:border-electric"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="py-16 md:py-24 bg-zinc-50 border-b border-zinc-200">
          <div className="container">
            {filtered.length === 0 ? (
              <p className="text-center py-20 text-zinc-500">
                No case studies match that filter.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                {filtered.map((c, idx) => (
                  <motion.article
                    key={c.slug}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: idx * 0.05 }}
                    className="group"
                    data-testid={`case-${idx}`}
                  >
                    <Link
                      to={`/case-studies/${c.slug}`}
                      className="bg-white p-7 md:p-8 flex flex-col h-full hover:bg-charcoal hover:text-white transition-colors"
                    >
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-overline text-electric">
                        {c.category}
                      </span>
                      <div className="flex items-center gap-2 text-zinc-400 group-hover:text-zinc-300">
                        {c.platform !== "android" && <Apple className="w-4 h-4" />}
                        {c.platform !== "ios" && <Play className="w-4 h-4" />}
                      </div>
                    </div>
                    <div className="font-display text-5xl md:text-6xl font-black text-electric leading-none mb-2">
                      {c.metric}
                    </div>
                    <p className="text-sm uppercase tracking-widest text-zinc-500 group-hover:text-zinc-300 mb-6">
                      {c.metricLabel}
                    </p>
                    <h3 className="font-display text-xl md:text-2xl font-bold leading-tight">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-[15px] text-zinc-600 group-hover:text-zinc-300 leading-relaxed">
                      {c.summary}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {c.services.map((s) => (
                        <span
                          key={s}
                          className="text-[11px] px-2 py-1 border border-zinc-300 text-zinc-700 group-hover:border-white/30 group-hover:text-zinc-200"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                    <div className="mt-auto pt-4 flex items-center justify-between text-xs text-zinc-500 group-hover:text-zinc-300 border-t border-zinc-200 group-hover:border-white/10">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" /> {c.market}
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold">
                        {c.duration} <ArrowUpRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Highlight quote */}
        <section className="py-20 md:py-24 bg-white">
          <div className="container text-center max-w-4xl mx-auto">
            <p className="text-overline text-electric mb-4">Client voice</p>
            <p className="font-display text-3xl md:text-4xl text-charcoal leading-snug font-bold text-balance">
              “Lumotis moved our food-delivery app from rank #29 to #3 on
              Google Play in three months. The ROI is unreal.”
            </p>
            <p className="mt-6 text-zinc-500 text-sm">
              — Priya Iyer, Founder · MealFox
            </p>
            <Link
              to="/contact"
              data-testid="cases-bottom-cta"
              className="mt-10 inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-400 transition-colors shadow-lg rounded-xl"
            >
              Become the next case study <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <Stats />
        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
